import React, { useEffect, useState } from 'react';

import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card } from '@buildoutinc/blueprint-react/ui/Card';
import { get } from 'helpers/api';

import type { LinkCardToolCall } from '../types';

// The record says where it lives rather than the card assuming it was fetched from there, so a
// listing can point at its project.
export interface LinkCardRecord {
  path: string;
  thumbnailUrl?: string | null;
}

interface Props<T extends LinkCardRecord> {
  children: (record: T) => React.ReactNode;
  path: string;
}

const MAX_CARDS = 3;

export function getCardPaths(toolCalls: LinkCardToolCall[]) {
  // One turn can surface the same record more than once - dedupe
  const allPaths = toolCalls.reduce<string[]>((paths, toolCall) => paths.concat(toolCall.metadata.paths), []);

  // Each card makes a request, so cap the number of cards instead of rendering all
  return Array.from(new Set(allPaths)).slice(0, MAX_CARDS);
}

export function LinkCard<T extends LinkCardRecord>({ children, path }: Props<T>) {
  const [record, setRecord] = useState<T | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setRecord(await get(path));
      } catch {
        // The reply already answered the question, so a card that never arrives is no loss.
      }
    })();
  }, [path]);

  if (!record) {
    return null;
  }

  return (
    <a className="text-decoration-none text-reset" href={record.path}>
      <Card>
        <Card.Body className="d-flex align-items-center gap-2">
          {record.thumbnailUrl && (
            <img alt="" className="flex-shrink-0 rounded" height={40} src={record.thumbnailUrl} width={40} />
          )}
          <div className="flex-grow-1 text-truncate">{children(record)}</div>
          <FontAwesomeIcon className="flex-shrink-0 text-muted" icon={faChevronRight} />
        </Card.Body>
      </Card>
    </a>
  );
}
